import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WishlistPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const [wishlist, setWishlist] = useState({ items: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !token) {
      navigate('/');
      return;
    }
    fetchWishlist();
  }, [user, token]);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/wishlist`);
      setWishlist(response.data);
      
      // Fetch product details
      if (response.data.items.length > 0) {
        const productPromises = response.data.items.map(item =>
          axios.get(`${API_URL}/products/${item.product_id}`).then(res => res.data).catch(() => null)
        );
        const productResults = await Promise.all(productPromises);
        setProducts(productResults.filter(Boolean));
      }
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await axios.delete(`${API_URL}/wishlist/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await addToCart(product.id, 0, 1);
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-8">My Wishlist</h1>

        {products.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-20 w-20 mx-auto text-muted-foreground/50 mb-6" />
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-4">
              Your wishlist is empty
            </h2>
            <p className="text-muted-foreground mb-8">
              Save products you like to your wishlist
            </p>
            <Link to="/products">
              <Button className="bg-primary hover:bg-primary/90 rounded-full px-8">
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden group">
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square overflow-hidden bg-stone-50">
                    <img
                      src={product.images?.[0] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>
                <div className="p-4">
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-medium text-stone-900 hover:text-primary transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-lg font-semibold text-primary mt-2">
                    ₹{product.variants?.[0]?.price || 0}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-primary hover:bg-primary/90 rounded-full"
                      size="sm"
                    >
                      <ShoppingCart className="h-4 w-4 mr-1" /> Add
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemove(product.id)}
                      className="text-destructive border-destructive/50 hover:bg-destructive/10 rounded-full"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
